

















































import { CLASSIFIED_SEARCH_NS } from '~/store/modules/shared/classifieds/search/state'
import { AxiosResponse } from 'axios'
import {
  MODIFY_ROW_DELETED,
  MODIFY_ROW_NON_PUBLIC,
  MODIFY_ROW_PUBLIC,
  MODIFY_ROW_SHOWN,
  MODIFY_ROW_TOUCHED
} from '~/store/modules/shared/classifieds/search/mutation-types'
import { hideFromPublicReasons } from '~/models/classified/constants'
import HideFromPublicModal from '~/components/car/classifieds/HideFromPublicModal.vue'
import ClassifiedService from '~/services/classified/ClassifiedService'
import { defineComponent, ref, computed } from '~/utils/nuxt3-migration'
import { ciEye } from '~/icons/source/solid/eye'
import { ciEyeSlash } from '~/icons/source/solid/eye-slash'
import { ciRefresh } from '~/icons/source/regular/refresh'
import { ciTrash } from '~/icons/source/regular/trash'
import { useAppConfirmationModal } from '~/compositions/app-confirmation-modal'
import { useDeps } from '~/compositions/dependency-container'
import { useNamespacedStore } from '~/compositions/store'
import { useSnackbar } from '~/compositions/snackbar'
import { useRequestError } from '~/compositions/request-error'

export default defineComponent({
  components: { HideFromPublicModal },
  setup() {
    const confirm = useAppConfirmationModal()
    const [classifiedService] = useDeps(ClassifiedService)
    const {
      getters: classifiedSearchGetters,
      commit: classifiedSearchCommit
    } = useNamespacedStore(CLASSIFIED_SEARCH_NS)
    const snackbar = useSnackbar()
    const handleRequestError = useRequestError()

    const hideFromPublicModalVisible = ref(false)
    const loading = ref(false)

    const allRowsIds = computed(() => classifiedSearchGetters('getAllRowsIds'))

    async function touchAllClassifiedsInPage() {
      loading.value = true

      try {
        const responses: AxiosResponse[] = await classifiedService.touchAll(
          allRowsIds.value
        )

        const successfulResponses = responses.filter(
          response => response.status === 200
        )

        successfulResponses.forEach(response => {
          classifiedSearchCommit(MODIFY_ROW_TOUCHED, {
            classifiedId: response.data.classified.id,
            modified: response.data.classified.modified
          })
        })

        snackbar.info(
          `Ανανεώθηκαν: ${
            successfulResponses.length
          }. Απέτυχαν: ${responses.length - successfulResponses.length}`
        )
      } catch (error) {
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }

    async function showAllClassifiedsInPage() {
      loading.value = true

      try {
        const responseTuples = await classifiedService.showAllToPublic(
          allRowsIds.value
        )

        responseTuples.forEach(responseTuple => {
          const [showToPublicResponse] = responseTuple

          classifiedSearchCommit(MODIFY_ROW_PUBLIC, {
            classifiedId: showToPublicResponse.data.classified.id
          })
          classifiedSearchCommit(MODIFY_ROW_SHOWN, {
            classifiedId: showToPublicResponse.data.classified.id
          })
        })
        snackbar.info('Εμφανίστηκαν')
      } catch (error) {
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }

    async function hideAllClassifiedsInPage({ reason, notes }) {
      loading.value = true

      try {
        const responses = await classifiedService.hideAllFromPublic(
          allRowsIds.value,
          reason,
          notes
        )
        const successfulResponses = responses.filter(
          response => response.status === 200
        )

        successfulResponses.forEach(response => {
          classifiedSearchCommit(MODIFY_ROW_NON_PUBLIC, {
            classifiedId: response.data.classified.id
          })
        })

        snackbar.info(
          `Κρύφτηκαν: ${
            successfulResponses.length
          }. Απέτυχαν: ${responses.length - successfulResponses.length}`
        )
      } catch (error) {
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }

    async function deleteAllClassifiedsInPage() {
      loading.value = true

      try {
        const responses: AxiosResponse[] = await classifiedService.deleteAll(
          allRowsIds.value
        )

        const successfulResponses = responses.filter(
          response => response.status === 200
        )

        successfulResponses.forEach(response => {
          classifiedSearchCommit(MODIFY_ROW_DELETED, {
            classifiedId: response.data.deleted_id
          })
        })

        snackbar.info(
          `Διαγράφηκαν: ${
            successfulResponses.length
          }. Απέτυχαν: ${responses.length - successfulResponses.length}`
        )
      } catch (error) {
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }

    function onTouchAllClassifiedsButtonClick() {
      confirm.show({
        title: 'Ανανέωσε όλες τις αγγελίες στη σελίδα',
        text: 'Θέλετε σίγουρα να ανανεώσετε όλες τις αγγελίες της σελίδας;',
        confirmButtonText: 'Ανανέωσε όλες τις αγγελίες στη σελίδα',
        variant: 'secondary',
        onConfirm: touchAllClassifiedsInPage
      })
    }

    function onShowAllClassifiedsButtonClick() {
      confirm.show({
        title: 'Δείξε όλες τις αγγελίες στη σελίδα',
        text:
          'Θέλετε σίγουρα να δείξετε όλες τις αγγελίες της σελίδας στο κοινό;',
        confirmButtonText: 'Δείξε όλες τις αγγελίες στη σελίδα',
        variant: 'success',
        onConfirm: showAllClassifiedsInPage
      })
    }

    function onDeleteAllClassifiedsButtonClick() {
      confirm.show({
        title: 'Διέγραψε όλες τις αγγελίες στη σελίδα',
        text: 'Θέλετε σίγουρα να διαγράψετε όλες τις αγγελίες της σελίδας;',
        confirmButtonText: 'Διέγραψε όλες τις αγγελίες στη σελίδα',
        variant: 'danger',
        onConfirm: deleteAllClassifiedsInPage
      })
    }

    return {
      hideFromPublicReasons,
      ciRefresh,
      ciEyeSlash,
      ciEye,
      ciTrash,
      loading,
      hideFromPublicModalVisible,
      onTouchAllClassifiedsButtonClick,
      onShowAllClassifiedsButtonClick,
      onDeleteAllClassifiedsButtonClick,
      hideAllClassifiedsInPage
    }
  }
})
